<script setup>
/* Vorschau der Player Card im Reiter Material: das obere Drittel der ersten Seite der echten, erzeugten Karte.
 *
 * Reine Anzeige (antippen öffnet nichts; der Download bleibt allein der Knopf). Die Karte entsteht über dieselbe
 * Funktion wie der Download (useProfilePdf.cardBlobFor, aus buildProfile); das PDF wird zu einem Bild der ersten
 * Seite gerendert (export/pdfPageImage.js, pdf.js nur bei Bedarf). Erzeugt wird erst, wenn der Abschnitt sichtbar ist;
 * Änderungen mit Verzögerung (usePlayerCardPreview). Die Größe ist fest reserviert — kein Layout-Sprung.
 * Fehler blenden nur die Vorschau aus, nie Download oder Reiter.
 */
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { usePlayerCardPreview } from '../../composables/usePlayerCardPreview.js';
import { useProfilePdf } from '../../composables/useProfilePdf.js';
import { buildProfile } from '../../domain/profileSheet.js';
import { layoutCard, previewCrop, PAGE } from '../../export/cardGeometry.js';
import { firstPageImage } from '../../export/pdfPageImage.js';

const props = defineProps({
  player: { type: Object, required: true },
});

const { t } = useI18n();
const { cardBlobFor, previewTransfers } = useProfilePdf();
/* Transferhistorie (F13): dieselbe Liste wie im Reiter Transfer Info; die Stub-Fassung in Tests hat sie nicht */
const transfersFor = (player) => (previewTransfers ? previewTransfers(player) : null);
const root = ref(null);

/* Datenstand: dieselbe Quelle wie die Karte selbst (buildProfile), plus Spieler und Saison */
const keyOf = (player) => {
  try {
    return `${player.id}|${JSON.stringify(buildProfile(player, { transfers: transfersFor(player) }))}`;
  } catch {
    return `${player.id}|${Date.now()}`;
  }
};

/* Zuschnitt an einer natürlichen Kante: unter der ersten Kachelreihe, aus der Seitengeometrie (cardGeometry.js) —
   nicht fest verdrahtet; bei weniger als vier Kacheln sind sie höher, der Schnitt wandert mit. */
const cropFor = (player) => {
  try {
    const lay = layoutCard(buildProfile(player, { transfers: transfersFor(player) }));
    return previewCrop(lay.tiles.count, lay.tiles.top);
  } catch {
    return previewCrop(0);
  }
};
const aspect = computed(() => `${PAGE.W} / ${(PAGE.H * cropFor(props.player)).toFixed(2)}`);

const preview = usePlayerCardPreview(() => props.player, {
  keyOf,
  render: async (player) => firstPageImage(await cardBlobFor(player), { crop: cropFor(player) }),
});

const alt = computed(() => t('material.cardPreviewAlt', { name: `${props.player.firstName || ''} ${props.player.lastName || ''}`.trim() }));

let observer = null;
onMounted(() => {
  if (typeof IntersectionObserver === 'function' && root.value) {
    observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        preview.start();
        observer.disconnect();
        observer = null;
      }
    });
    observer.observe(root.value);
  } else {
    preview.start();
  }
});
onBeforeUnmount(() => {
  if (observer) observer.disconnect();
  preview.dispose();
});
</script>

<template>
  <div ref="root" data-testid="card-preview">
    <p v-if="preview.status.value === 'failed'" class="cp-note" data-testid="card-preview-failed">
      {{ $t('material.cardPreviewNA') }}
    </p>
    <div v-else class="cp-sheet" :style="{ aspectRatio: aspect }">
      <img v-if="preview.url.value" :src="preview.url.value" :alt="alt" class="cp-img" draggable="false" data-testid="card-preview-img" />
      <div v-else class="cp-wait" data-testid="card-preview-loading" role="status">{{ $t('material.cardPreparing') }}</div>
    </div>
  </div>
</template>

<style scoped>
.cp-sheet {
  position: relative;
  /* Eigene Ebene ganz unten: Die Maske unten würde das Blatt sonst auf eine eigene Compositing-Ebene legen, die in
     WebKit über die feste Reiterleiste (PlayerTabs.vue) malen kann. */
  z-index: 0;
  isolation: isolate;
  width: 100%;
  max-width: 480px;
  /* Seitenverhältnis fest reserviert (inline aus der Seitengeometrie) — kein Sprung beim Laden */
  aspect-ratio: 210 / 150;
  margin-bottom: 12px;
  overflow: hidden;
  /* das Blatt bleibt in beiden Themen weiß */
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.16);
  /* weich ausblenden: das Blatt selbst läuft in Transparenz aus, der Hintergrund des Hub scheint durch — kein
     Farbvergleich nötig, in Hell und Dunkel gleich sauber */
  -webkit-mask-image: linear-gradient(to bottom, #000 0%, #000 88%, transparent 100%);
  mask-image: linear-gradient(to bottom, #000 0%, #000 88%, transparent 100%);
}
.cp-img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top;
  user-select: none;
  pointer-events: none;
}
.cp-wait {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #77768a;
  font-size: 0.8rem;
  background: linear-gradient(100deg, #f4f5f8 30%, #fff 50%, #f4f5f8 70%);
}
.cp-note {
  margin: 0 0 10px;
  font-size: 0.8rem;
  color: var(--dim);
}
@media (prefers-reduced-motion: no-preference) {
  .cp-wait {
    background-size: 200% 100%;
    animation: cp-shimmer 1.6s linear infinite;
  }
}
@keyframes cp-shimmer {
  from { background-position: 100% 0; }
  to { background-position: -100% 0; }
}
</style>
